/**
 * ClipsTab — platform-agnostic content pool.
 *
 * Merges ALL short-form and recent-content sources the creator has,
 * regardless of platform. For a Twitch-heavy creator: clips dominate.
 * For a Kick + YT + TikTok creator (no Twitch): TikToks + YouTube videos
 * + Kick VODs fill the pool. The label is "Clips" but the content is
 * whatever the creator actually publishes.
 *
 * Sources merged:
 *   · Twitch clips        (TWITCH)
 *   · Twitch VODs         (TWITCH)
 *   · Kick VODs           (KICK)
 *   · YouTube videos      (YOUTUBE)
 *   · TikTok videos       (TIKTOK)
 *
 * Sorted by recency. Mixed types in one grid; source badge on each card.
 */

import React, { useState } from "react";
import { FaPlay, FaEye, FaClock, FaTiktok } from "react-icons/fa";


import { useGetDropTwitchClips } from "@/hooks/drop/useGetDropTwitchClips";
import { useGetDropTwitchVods } from "@/hooks/drop/useGetDropTwitchVods";
import { useGetDropKickVods } from "@/hooks/drop/useGetDropKickVods";
import { useGetDropYouTubeVideos } from "@/hooks/drop/useGetDropYouTubeVideos";
import { useGetDropTikTokVideos } from "@/hooks/drop/useGetDropTikTokVideos";
import { formatDuration, formatViews, timeAgo, platformIcon, isPlaceholderTwitchThumb, type StreamingSource, type SelectedClip } from "./shared";

interface MergedItem {
    key: string;
    id: string;
    source: StreamingSource | "TIKTOK";
    title: string;
    url: string;
    thumbnailUrl: string;
    when: string;
    durationSeconds: number;
    viewCount: number;
    isVertical?: boolean;
    hlsUrl?: string;
}

interface ClipsTabProps {
    /** Drop slug — drives every content query in this component. */
    dropSlug?: string;
    hubName?: string;
    /** Currently-selected clip id (from HubCreatorSection) — drives the highlight state */
    selectedClipId?: string;
    /**
     * Click handler. Called with a `SelectedClip` shape (or null if click should
     * be ignored — e.g. TikTok plays in the social mosaic, not the main player).
     */
    onSelectClip?: (clip: SelectedClip | null) => void;
}

export const ClipsTab: React.FC<ClipsTabProps> = ({ dropSlug, hubName, selectedClipId, onSelectClip }) => {
    /* Drop content endpoints only — see HighlightsRow for the same pattern. */
    const dropTwitchClipsQuery = useGetDropTwitchClips(dropSlug);
    const dropTwitchVodsQuery = useGetDropTwitchVods(dropSlug);
    const dropKickVodsQuery = useGetDropKickVods(dropSlug);
    const dropYoutubeQuery = useGetDropYouTubeVideos(dropSlug);
    const dropTiktokQuery = useGetDropTikTokVideos(dropSlug);
    const twitchClipsQuery = dropTwitchClipsQuery;
    const twitchVodsQuery = dropTwitchVodsQuery;
    const kickVodsQuery = dropKickVodsQuery;
    const youtubeQuery = dropYoutubeQuery;
    const tiktokQuery = dropTiktokQuery;

    const twitchClips = (twitchClipsQuery.data as { clips?: Array<{
        id: string; title: string; url: string; thumbnailUrl: string;
        createdAt: string; durationSeconds: number; viewCount: number;
    }> } | null | undefined)?.clips ?? [];
    const twitchVods = (twitchVodsQuery.data as { vods?: Array<{
        id: string; title: string; url: string; thumbnailUrl: string;
        createdAt: string; durationSeconds: number; viewCount: number;
    }> } | null | undefined)?.vods ?? [];
    const kickVods = (kickVodsQuery.data as { vods?: Array<{
        id: string; title: string; url: string; thumbnailUrl: string;
        createdAt: string; durationSeconds: number; viewCount: number;
    }> } | null | undefined)?.vods ?? [];
    const youtubeVids = (youtubeQuery.data as { videos?: Array<{
        videoId: string; title: string; thumbnailUrl: string;
        publishedAt: string; durationSeconds?: number; viewCount?: number;
    }> } | null | undefined)?.videos ?? [];
    const tiktokVids = (tiktokQuery.data as { videos?: Array<{
        id: string; url: string; authorHandle: string; title?: string;
        embedHtml?: string; thumbnailUrl?: string;
    }> } | null | undefined)?.videos ?? [];

    const all: MergedItem[] = [
        ...twitchClips.map(c => ({
            key: `tc-${c.id}`, id: c.id, source: "TWITCH" as const, title: c.title, url: c.url,
            thumbnailUrl: c.thumbnailUrl, when: c.createdAt,
            durationSeconds: c.durationSeconds, viewCount: c.viewCount,
        })),
        ...twitchVods.map(v => ({
            key: `tv-${v.id}`, id: v.id, source: "TWITCH" as const, title: v.title, url: v.url,
            thumbnailUrl: v.thumbnailUrl, when: v.createdAt,
            durationSeconds: v.durationSeconds, viewCount: v.viewCount,
        })),
        ...kickVods.map(v => ({
            key: `kv-${v.id}`, id: v.id, source: "KICK" as const, title: v.title, url: v.url,
            thumbnailUrl: v.thumbnailUrl, when: v.createdAt,
            durationSeconds: v.durationSeconds, viewCount: v.viewCount,
            hlsUrl: (v as { hlsUrl?: string }).hlsUrl,
        })),
        ...youtubeVids.map(v => ({
            key: `yt-${v.videoId}`, id: v.videoId, source: "YOUTUBE" as const, title: v.title,
            url: `https://www.youtube.com/watch?v=${v.videoId}`,
            thumbnailUrl: v.thumbnailUrl, when: v.publishedAt,
            durationSeconds: v.durationSeconds ?? 0, viewCount: v.viewCount ?? 0,
        })),
        ...tiktokVids.map(v => ({
            key: `tt-${v.id}`, id: v.id, source: "TIKTOK" as const, title: v.title ?? "TikTok",
            url: v.url, thumbnailUrl: v.thumbnailUrl ?? "",
            /* TikTok oEmbed doesn't return upload date — sort to the bottom */
            when: new Date(0).toISOString(),
            durationSeconds: 0, viewCount: 0, isVertical: true,
        })),
    ];
    all.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());

    /*
     * Two-stage cap: initial 8 fits one clean row at desktop (the grid is
     * sized for 8 columns), expanded 24 covers the next two rows. Avoids
     * the awkward orphan-card-on-row-2 look while keeping the page from
     * drowning in clips a creator with hundreds of past streams produces.
     */
    const INITIAL_CAP = 8;
    const EXPANDED_CAP = 24;
    const [expanded, setExpanded] = useState(false);
    const visible = all.slice(0, expanded ? EXPANDED_CAP : INITIAL_CAP);
    const hasMore = all.length > visible.length;

    if (all.length === 0) {
        return (
            <div className="hcs-empty">
                <FaPlay />
                <h3>No clips yet</h3>
                <p>Recent clips and videos from {hubName ?? "this creator"} across Twitch, Kick, YouTube, and TikTok will appear here.</p>
            </div>
        );
    }

    return (
        <div className="hcs-clips-tab">
            <div className="hcs-grid hcs-grid--clips">
                {visible.map(c => {
                /*
                 * TikTok cards always link out — TikTok's vertical embed lives
                 * in the SOCIAL mosaic, not the horizontal main player.
                 *
                 * Kick VODs require an HLS source URL to play inline (no public
                 * VOD iframe). When Kick's API returned the VOD metadata but
                 * not the m3u8 source (older / not-yet-processed broadcasts),
                 * link out instead — the alternative is a blank inline player.
                 *
                 * Everything else (Twitch / YouTube clips + VODs) promotes
                 * into the StreamPanel inline.
                 */
                    const kickMissingHls = c.source === "KICK" && !c.hlsUrl;
                    const promotable = c.source !== "TIKTOK" && !kickMissingHls && onSelectClip;
                    const isSelected = selectedClipId === c.id;
                    const handleClick = (e: React.MouseEvent) => {
                        if (!promotable) return;
                        e.preventDefault();
                        onSelectClip!({
                            id: c.id,
                            source: c.source,
                            url: c.url,
                            title: c.title,
                            thumbnailUrl: c.thumbnailUrl,
                            hlsUrl: c.hlsUrl,
                        });
                    };
                    return (
                        <a
                            className={`hcs-card${isSelected ? " is-selected" : ""}`}
                            key={c.key}
                            href={c.url}
                            target={promotable ? undefined : "_blank"}
                            rel={promotable ? undefined : "noopener noreferrer"}
                            onClick={handleClick}
                        >
                            <div className={`hcs-card__thumb-wrap${c.isVertical ? " hcs-card__thumb-wrap--portrait" : ""}`}>
                                {c.thumbnailUrl && !isPlaceholderTwitchThumb(c.thumbnailUrl)
                                    ? <img className="hcs-card__thumb" src={c.thumbnailUrl} alt={c.title} loading="lazy" />
                                    : <div className="hcs-card__thumb hcs-card__thumb--empty">
                                        {c.source === "TIKTOK" ? <FaTiktok /> : <FaPlay />}
                                    </div>}
                                <span className={`hcs-card__badge hcs-card__badge--${c.source.toLowerCase()}`}>
                                    {c.source === "TIKTOK" ? <FaTiktok /> : platformIcon(c.source)}
                                </span>
                                {c.durationSeconds > 0 && <span className="hcs-card__duration">{formatDuration(c.durationSeconds)}</span>}
                                {isSelected && <span className="hcs-card__playing">PLAYING</span>}
                            </div>
                            <div className="hcs-card__body">
                                <div className="hcs-card__title">{c.title || `${c.source} content`}</div>
                                <div className="hcs-card__meta">
                                    {c.viewCount > 0 ? <span><FaEye /> {formatViews(c.viewCount)}</span> : null}
                                    {c.source !== "TIKTOK" && <span><FaClock /> {timeAgo(c.when)}</span>}
                                </div>
                            </div>
                        </a>
                    );
                })}
            </div>
            {hasMore &&
                <button type="button" className="hcs-clips-tab__more"
                    onClick={() => setExpanded(true)}>
                    Show more clips
                </button>
            }
        </div>
    );
};
